import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Phone } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { CallRow } from '@/components/dashboard/CallRow';
import { ChipFilter } from '@/components/calls/ChipFilter';
import type { FilterKey } from '@/components/calls/ChipFilter';
import { MOCK_CALLS } from '@/data/mock';

export const Route = createFileRoute('/klici/')({
  component: KliciPage,
});

const CHIPS: { key: FilterKey; label: string }[] = [
  { key: 'all',       label: 'Vsi' },
  { key: 'new',       label: 'Novi' },
  { key: 'converted', label: 'Pretvorjeni' },
  { key: 'missed',    label: 'Zgrešeni' },
];

function KliciPage() {
  const [filter, setFilter] = useState<FilterKey>('all');

  const filtered = useMemo(() => {
    if (filter === 'all')       return MOCK_CALLS;
    if (filter === 'new')       return MOCK_CALLS.filter(c => c.status === 'new');
    if (filter === 'converted') return MOCK_CALLS.filter(c => c.status === 'quote' || c.status === 'book');
    return MOCK_CALLS.filter(c => c.status === 'missed');
  }, [filter]);

  return (
    <AppShell pageTitle="Klici" crumb="TradeCraft · Klici">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        marginBottom: '1.25rem',
        flexWrap: 'wrap',
      }}>
        <ChipFilter options={CHIPS} active={filter} onChange={setFilter} />
        <span style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.72rem',
          color: 'var(--app-subtle)',
        }}>
          {filtered.length} klic{filtered.length !== 1 ? 'ev' : ''}
        </span>
      </div>

      {filtered.length > 0 ? (
        <div className="app-card" style={{ padding: '0 1.25rem' }}>
          {filtered.map(call => (
            <CallRow key={call.id} call={call} />
          ))}
        </div>
      ) : (
        <EmptyKlici filter={filter} />
      )}
    </AppShell>
  );
}

const EMPTY_MESSAGES: Record<FilterKey, string> = {
  all:       'Danes ni bilo klicev.',
  new:       'Ni novih klicev.',
  converted: 'Ni pretvorjenih klicev.',
  missed:    'Ni zgrešenih klicev.',
};

function EmptyKlici({ filter }: { filter: FilterKey }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      padding: '5rem 2rem',
      textAlign: 'center',
    }}>
      <span style={{
        width: '48px',
        height: '48px',
        borderRadius: '50%',
        background: 'var(--app-raised)',
        border: '1px solid var(--app-line2)',
        display: 'grid',
        placeItems: 'center',
        color: 'var(--app-subtle)',
      }}>
        <Phone size={20} strokeWidth={1.4} />
      </span>
      <p style={{ color: 'var(--app-muted)', fontSize: '0.9rem', margin: 0 }}>
        {EMPTY_MESSAGES[filter]}
      </p>
    </div>
  );
}
